import React from "react";
import styled from "styled-components";
import _, { first, forEach } from "lodash";
import { GoogleSpreadsheet } from "google-spreadsheet";
import { formatCompanyName } from "../utils/utilities";

import Logo from "../images/logo.webp";
import HeroTwo from "../images/hero-2.webp";
import AdditionalMessage from "./AdditionalMessage";

// Spreadsheet stuff...
const SPREADSHEET_ID = process.env.REACT_APP_SPREADSHEET_ID;
const CLIENT_EMAIL = process.env.REACT_APP_CLIENT_EMAIL;
const PRIVATE_KEY = (process.env.REACT_APP_PRIVATE_KEY || "").replace(
  /\\n/g,
  "\n"
);
const API_KEY = process.env.REACT_APP_API_KEY;
const doc = new GoogleSpreadsheet(SPREADSHEET_ID);

const UK_POSTCODE_REGEX = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/;

function formatPostcode(value) {
  const compact = (value || "").toUpperCase().replace(/\s+/g, "");
  if (compact.length < 5) {
    return compact;
  }
  return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
}

function isValidPostcode(value) {
  return UK_POSTCODE_REGEX.test(formatPostcode(value));
}

const TotalBox = (props) => {
  const {
    productDetails,
    setProductDetails,
    discounts,
    postage,
    useForm,
    companyDetails,
    setShowOverlay,
    showOverlay,
    confirmation,
    firstDiscountTier,
    messageData,
    audiencePath,
    activeCompanyAudience,
    existingCompany,
    companyFilter,
    xid
  } = props;
  const [seconds, setSeconds] = React.useState(0);
  const [productsTotal, setProductsTotal] = React.useState(0);
  const [
    productsTotalWithoutDiscount,
    setProductsTotalWithoutDiscount
  ] = React.useState(0);
  const [netOnlyTotal, setNetOnlyTotal] = React.useState(0);
  const [discountTiers, setDiscountTiers] = React.useState();
  const [postageTiers, setPostageTiers] = React.useState();
  const [finalDiscount, setFinalDiscount] = React.useState(0);
  const [totalWithDiscount, setTotalWithDiscount] = React.useState(0);
  const [finalPostage, setFinalPostage] = React.useState(0);
  const isDisabled = useForm ? !companyDetails.company : false;
  const [endDiscount, setEndDiscount] = React.useState(0);
  const [isActive, setIsActive] = React.useState(false);
  const [message, setMessage] = React.useState();
  const [deliveryPostcode, setDeliveryPostcode] = React.useState("");

  // STALE CLOSURE WORKAROUND
  // getTotal() is called from inside a setInterval that was set up in a useLayoutEffect([]).
  // That closure captures productDetails once at mount time and never sees later state updates.
  // Previously this worked accidentally because productDetails was mutated in place (same array
  // reference). Now we use functional setProductDetails (prev => [...prev]) which creates a new
  // array on every update — the stale closure would always read the original empty array.
  //
  // Fix: keep a ref that is always updated to the latest productDetails. getTotal() reads from
  // productDetailsRef.current rather than the closed-over productDetails variable, so it always
  // sees the current state without needing to re-create the interval.
  const productDetailsRef = React.useRef(productDetails);
  React.useEffect(() => {
    productDetailsRef.current = productDetails;
  }, [productDetails]);

  // Message data
  const afterMessage = messageData["Add message"];
  const totalBoxMessage = messageData["Box message"];
  const basketMessage = messageData["Basket total"];
  const volumeMessage = messageData["Volume discount"];
  const tierMessage = messageData["Tier message"];

  // Confirmation data
  const confirmationMessage = confirmation["Message"];
  const dispatchShow = confirmation["Show automatic dispatch"];
  const dispatchMessage = confirmation["Dispatch message"];
  const additionalMessage = confirmation["Additional Message"];
  const inputRef = React.useRef();

  // Get the company name
  let search = window.location.search;
  let params = new URLSearchParams(search);
  let companyName = params.get("company");
  const isNewAccount = Boolean(params.get("newaccount"));
  const askForPostcode = !isNewAccount;
  const postcodeOk = !askForPostcode || isValidPostcode(deliveryPostcode);

  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }

    handleNewCompany();
  }, []);

  // Data to send
  let dataToSend = {};

  React.useLayoutEffect(() => {
    const interval = setInterval(() => {
      setSeconds((seconds) => seconds + 1);
      getTotal();
    }, 500);

    setDiscountTiers(getDiscountTiers());
    setPostageTiers(getPostageTiers());

    return () => clearInterval(interval);
  }, []);

  // BASKET QUANTITY HANDLERS
  // These use the functional form of setProductDetails (prev => ...) so they always operate on
  // the latest state, never a stale closure captured at render time. Reading item from prev[index]
  // inside the updater guarantees we don't accidentally overwrite concurrent updates from other
  // Product components.
  function handleBasketSubtract(index) {
    setProductDetails((prev) => {
      const item = prev[index];
      if (!item) return prev;
      const next = [...prev];
      next[index] = {
        ...item,
        quantity: Math.max(0, item.quantity - parseInt(item.packQTY))
      };
      return next;
    });
  }

  function handleBasketAdd(index) {
    setProductDetails((prev) => {
      const item = prev[index];
      if (!item) return prev;
      const next = [...prev];
      next[index] = {
        ...item,
        quantity: item.quantity + parseInt(item.packQTY)
      };
      return next;
    });
  }

  function handleBasketRemove(index) {
    setProductDetails((prev) => {
      const item = prev[index];
      if (!item) return prev;
      const next = [...prev];
      next[index] = { ...item, quantity: 0 };
      return next;
    });
  }

  async function handleNewCompany() {
    await doc.useServiceAccountAuth({
      client_email: CLIENT_EMAIL,
      private_key: PRIVATE_KEY
    });
    await doc.loadInfo(); // loads document properties and worksheets
    const companiesSheet = doc.sheetsById[604849251];

    // This checks for the existing company check used in App.js as well as checking new
    // form submissions with the companies list in the google sheets.
    if (existingCompany === false) {
      let dontAllowCompany;

      companyFilter &&
        companyFilter.map((item) => {
          if (item["Company name"] === companyDetails.company) {
            dontAllowCompany = true;
          }
        });

      if (!dontAllowCompany) {
        companiesSheet.addRow({
          "Company name": useForm
            ? companyDetails.company
            : formatCompanyName(companyName)
        });
      }
    }
  }

  function getDiscountTiers() {
    let discountsArray = [];
    discounts.map((item, index) => {
      // We set the discount tier to the same as the audience path
      // If there is no audience path, we set it to be the default discount percentage.
      if (item["Discount tier"] && _.get(item, activeCompanyAudience)) {
        discountsArray.push({
          tier: Number(item["Discount tier"]),
          discount: Number(item[activeCompanyAudience])
        });
      } else if (item["Discount tier"] && _.get(item, audiencePath)) {
        discountsArray.push({
          tier: Number(item["Discount tier"]),
          discount: Number(item[audiencePath])
        });
      } else if (item["Discount tier"] && item["Discount percentage"]) {
        discountsArray.push({
          tier: Number(item["Discount tier"]),
          discount: Number(item["Discount percentage"])
        });
      }
    });

    const filteredArray = discountsArray.filter(function(value) {
      return !Number.isNaN(value);
    });

    return filteredArray;
  }

  function getPostageTiers() {
    let postageArray = [];
    postage.map((item, index) => {
      if (item["Tier"] && _.get(item, activeCompanyAudience)) {
        postageArray.push({
          tier: Number(item["Tier"]),
          price: Number(item[activeCompanyAudience])
        });
      } else if (item["Tier"] && _.get(item, audiencePath)) {
        postageArray.push({
          tier: Number(item["Tier"]),
          price: Number(item[audiencePath])
        });
      } else if (item["Tier"] && item["Price"]) {
        postageArray.push({
          tier: Number(item["Tier"]),
          price: Number(item["Price"])
        });
      }
    });

    const filteredArray = postageArray.filter(function(value) {
      return !Number.isNaN(value);
    });

    return filteredArray;
  }

  function getTotal() {
    let productTotal = 0;
    let productWithoutDiscountTotal = 0;
    let netTotal = 0;
    productDetailsRef.current.map((item) => {
      if (item && item.quantity && item.netOnly) {
        netTotal = netTotal + item.quantity * Number(item.tradeCost);
      } else if (item && item.quantity) {
        const newProduct = item.quantity * Number(item.tradeCost);
        const newProductNoDiscount = item.quantity * Number(item.price / 1.2);

        productTotal = productTotal + newProduct;
        productWithoutDiscountTotal =
          productWithoutDiscountTotal + newProductNoDiscount;
      }
    });
    setProductsTotal(productTotal);
    setProductsTotalWithoutDiscount(productWithoutDiscountTotal);
    setNetOnlyTotal(netTotal);
  }

  async function handleSubmit() {
    await doc.useServiceAccountAuth({
      client_email: CLIENT_EMAIL,
      private_key: PRIVATE_KEY
    });
    await doc.loadInfo(); // loads document properties and worksheets
    const sheet = doc.sheetsById[1304132677]; // or use doc.sheetsById[id] or doc.sheetsByTitle[title]
    const companiesSheet = doc.sheetsById[604849251];

    // Get all the prodducts with quantity
    let orderDetails = [];
    productDetails.map((item) => {
      if (item && item.quantity > 0) {
        const itemDetails = item.netOnly
          ? `${item.name} (${item.packQTY}): trade price £${Number(
              item.tradeCost
            ).toFixed(2)}, no discount - ${item.quantity} ordered \n`
          : `${item.name} (${item.packQTY}): ${item.additionalDiscount}% (line discount) - ${item.quantity} ordered \n`;
        orderDetails.push(itemDetails);
      }
    });

    const baseCompany = useForm
      ? companyDetails.company
      : formatCompanyName(companyName);
    const companyWithPostcode = askForPostcode
      ? `${baseCompany} (${formatPostcode(deliveryPostcode)})`
      : baseCompany;

    // Format data to send to sheets.
    dataToSend = {
      ...dataToSend,
      Company: companyWithPostcode,
      Contact: useForm ? companyDetails.contact : "",
      Telephone: useForm ? companyDetails.telephone : "",
      Email: companyDetails.email,
      "Address 1": useForm ? companyDetails.addressone : "",
      "Address 2": useForm ? companyDetails.addresstwo : "",
      Town: useForm ? companyDetails.town : "",
      Postcode: useForm ? companyDetails.postcode : "",
      "Other notes": useForm ? companyDetails.notes : "",
      "Order details": orderDetails.join("").trim(),
      Subtotal: (productsTotalWithoutDiscount + netOnlyTotal).toFixed(2),
      Discount: endDiscount,
      "Total discount": `£${(
        productsTotalWithoutDiscount -
        productsTotalWithoutDiscount * (endDiscount * 0.01) +
        netOnlyTotal
      ).toFixed(2)}`,
      Postage: finalPostage,
      Total: (
        productsTotalWithoutDiscount * (1 - endDiscount * 0.01) +
        netOnlyTotal +
        finalPostage
      ).toFixed(2),
      "Immediate dispatch": isActive,
      Message: message ? message : "",
      ...(xid ? { XID: xid } : {})
    };

    // Add row to sheets with data to send
    sheet.addRow(dataToSend);

    // Show the overlay
    setShowOverlay(true);
  }

  const DiscountComponent = (props) => {
    const { discountTiers, setFinalDiscount } = props;
    if (!discountTiers) {
      return null;
    }

    let discount = 0;
    let totalDiscount = 0;
    let tierDifference;
    let nextTierDiscount;

    discountTiers.map((item, index) => {
      if (productsTotal >= item.tier) {
        // We get the discount from the discount tier and we also get the index
        discount = item.discount;
        totalDiscount = item.discount;

        // Get the tier difference

        // We check for the next tier and calculate the difference if there is no next tier, it's none.
        if (discountTiers[index + 1]) {
          // Get the next tier difference
          tierDifference = discountTiers[index + 1].tier - productsTotal;

          // Get the next tier discount
          nextTierDiscount = discountTiers[index + 1].discount;
        } else {
          nextTierDiscount = "none";
        }
      }
    });

    // Remove the first tier of discount from the discount
    discount = discount - firstDiscountTier;

    React.useEffect(() => {
      // Sets the final discount by getting the discount percentage
      setFinalDiscount(discount);

      // Gets the total with discount
      if (discount !== 0) {
        setTotalWithDiscount(productsTotal - productsTotal * (discount * 0.01));
      } else {
        setTotalWithDiscount(productsTotal);
      }
    }, [discountTiers]);

    setEndDiscount(totalDiscount);

    return (
      <>
        <StyledPara>
          <span>
            {volumeMessage} ({discount}%)
          </span>
          - £{(productsTotalWithoutDiscount * (discount * 0.01)).toFixed(2)}
        </StyledPara>
        <StyledHr />
        <StyledPara>
          <span>Order total</span> £
          {(
            productsTotalWithoutDiscount -
            productsTotalWithoutDiscount * (totalDiscount * 0.01) +
            netOnlyTotal
          ).toFixed(2)}
        </StyledPara>
        <SmallPara>Total Discount: {totalDiscount}%</SmallPara>
        <PostageComponent
          postageTiers={postageTiers}
          setFinalPostage={setFinalPostage}
        />
      </>
    );
  };

  const DiscountTiersComponent = (props) => {
    const { discountTiers, setFinalDiscount } = props;
    if (!discountTiers) {
      return null;
    }

    let discount = 0;
    let activeIndex = 0;
    let tierDifference;
    let nextTierDiscount;

    discountTiers.map((item, index) => {
      if (productsTotal >= item.tier) {
        discount = item.discount;
        activeIndex = index;
      }

      const isLargeNumber = (element) => element.discount > discount;
      const discountIndex = discountTiers.findIndex(isLargeNumber);

      if (discountIndex !== -1) {
        tierDifference = discountTiers[discountIndex].tier - productsTotal;
        nextTierDiscount = discountTiers[discountIndex].discount;
      } else {
        nextTierDiscount = "none";
      }
    });

    React.useEffect(() => {
      // Sets the final discount by getting the discount percentage
      setFinalDiscount(discount);

      // Gets the total with discount
      if (discount !== 0) {
        setTotalWithDiscount(productsTotal - productsTotal * (discount * 0.01));
      } else {
        setTotalWithDiscount(productsTotal);
      }
    }, [discountTiers]);

    return (
      <>
        {tierDifference && nextTierDiscount && nextTierDiscount !== "none" && (
          <>
            <SmallPara>{firstDiscountTier}% standard discount</SmallPara>
            <SmallPara>
              {afterMessage} £{tierDifference.toFixed(2)} {tierMessage}{" "}
              {nextTierDiscount - discount}%
            </SmallPara>
          </>
        )}
        {nextTierDiscount === "none" && (
          <p>
            <StyledItalic>You're getting the best savings.</StyledItalic>
          </p>
        )}
      </>
    );
  };

  const PostageComponent = (props) => {
    const { postageTiers, setFinalPostage } = props;
    if (!postageTiers) {
      return null;
    }

    let price = postageTiers[0].price;

    postageTiers.map((item, index) => {
      if (productsTotal > item.tier) {
        price = item.price;
      }
    });

    const isZero = (element) => element.price === 0;
    const isFree = postageTiers.findIndex(isZero);
    const freePrice = postageTiers[isFree].tier;

    React.useEffect(() => {
      setFinalPostage(price);
    }, [postageTiers]);

    return (
      <>
        <StyledPara>
          <span>
            <StyledItalicSmall>
              {totalBoxMessage && totalBoxMessage} £{freePrice})
            </StyledItalicSmall>
          </span>
          <span>£{price.toFixed(2)}</span>
        </StyledPara>
      </>
    );
  };

  const DispatchComponent = (props) => {
    const { messageData } = props;

    if (!messageData || dispatchShow === "FALSE") {
      return null;
    }

    function handleButtonClick() {
      setIsActive(!isActive);
    }

    return (
      <DispatchDetail>
        <SmallPara className="add-margin">
          {dispatchMessage
            ? dispatchMessage
            : "Tick to authorise for immediate dispatch. If unticked, you will receive a formal quote which you will then be able to accept."}
        </SmallPara>
        <div>
          <Checkbox
            onClick={handleButtonClick}
            className={`${isActive ? "active" : ""}`}
          />
        </div>
      </DispatchDetail>
    );
  };

  const hasItems = productDetails.some((item) => item && item.quantity > 0);

  return (
    <>
      <TotalBoxContainer>
        <StyledTotalBox>
          <StyledHeader>Order summary</StyledHeader>
          {hasItems && (
            <BasketItemsSection>
              <BasketItemsLabel>Items in your order</BasketItemsLabel>
              {productDetails.map((item, index) => {
                if (!item || item.quantity <= 0) return null;
                const lineTotal = (
                  item.quantity * Number(item.tradeCost)
                ).toFixed(2);
                return (
                  <BasketItem key={index}>
                    <BasketItemHeader>
                      <BasketItemName
                        dangerouslySetInnerHTML={{ __html: item.name }}
                      />
                      <BasketRemoveBtn
                        onClick={() => handleBasketRemove(index)}
                        title="Remove item"
                      >
                        &#10005;
                      </BasketRemoveBtn>
                    </BasketItemHeader>
                    <BasketItemFooter>
                      <BasketQtyControl>
                        <BasketBtn onClick={() => handleBasketSubtract(index)}>
                          &#8722;
                        </BasketBtn>
                        <BasketQty>{item.quantity}</BasketQty>
                        <BasketBtn onClick={() => handleBasketAdd(index)}>
                          +
                        </BasketBtn>
                      </BasketQtyControl>
                      <BasketLineTotal>£{lineTotal}</BasketLineTotal>
                    </BasketItemFooter>
                  </BasketItem>
                );
              })}
              <StyledHr />
            </BasketItemsSection>
          )}
          <StyledPara>
            <span>{basketMessage}</span>{" "}
            <span>£{productsTotal.toFixed(2)}</span>
          </StyledPara>
          {netOnlyTotal > 0 && (
            <>
              <StyledPara>
                <span>Trade-price items</span>{" "}
                <span>£{netOnlyTotal.toFixed(2)}</span>
              </StyledPara>
              <SmallPara>No discount applies to these items</SmallPara>
            </>
          )}
          <DiscountTiersComponent
            discountTiers={discountTiers}
            setFinalDiscount={setFinalDiscount}
          />
          <DiscountComponent
            discountTiers={discountTiers}
            setFinalDiscount={setFinalDiscount}
          />
          <StyledHr />
          <RightContainer>
            <InnerRightContainer>
              <p>
                <span>
                  Amount due: £
                  {(
                    productsTotalWithoutDiscount * (1 - endDiscount * 0.01) +
                    netOnlyTotal +
                    finalPostage
                  ).toFixed(2)}
                </span>
              </p>
              <SmallPara>(ex vat)</SmallPara>
              {askForPostcode && (
                <PostcodeField>
                  <PostcodeLabel htmlFor="delivery-postcode">
                    Delivery postcode
                  </PostcodeLabel>
                  <PostcodeInput
                    id="delivery-postcode"
                    type="text"
                    autoComplete="postal-code"
                    placeholder="e.g. BN1 6XA"
                    maxLength={8}
                    value={deliveryPostcode}
                    onChange={(e) =>
                      setDeliveryPostcode(e.target.value.toUpperCase())
                    }
                    onBlur={() =>
                      setDeliveryPostcode(formatPostcode(deliveryPostcode))
                    }
                  />
                  <PostcodeHint>
                    Used to verify the delivery address we have on file
                  </PostcodeHint>
                </PostcodeField>
              )}
              <StyledButton
                type="submit"
                onClick={handleSubmit}
                disabled={
                  productsTotal + netOnlyTotal === 0 ||
                  isDisabled ||
                  !postcodeOk
                }
                value="Submit order"
              />
            </InnerRightContainer>
          </RightContainer>
          <DispatchComponent messageData={messageData} />
          <div key="something">
            <AdditionalMessage
              messageData={messageData}
              dataToSend={dataToSend}
              message={message}
              setMessage={setMessage}
              additionalMessage={additionalMessage}
            />
          </div>
        </StyledTotalBox>
      </TotalBoxContainer>
      <StyledOverlay className={showOverlay ? "show" : ""}>
        <div>
          <StyledH2>
            {confirmationMessage
              ? confirmationMessage
              : "Thanks for your order. You should receive a confirmation via email soon."}
          </StyledH2>
          <StyledLink href="https://www.wzis.dog/">Back to site</StyledLink>
        </div>
      </StyledOverlay>
    </>
  );
};
export default TotalBox;

const BasketItemsSection = styled.div`
  margin-bottom: 14px;
`;

const BasketItemsLabel = styled.p`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #fab528;
  margin: 0 0 8px 0;
  font-weight: bold;
`;

const BasketItem = styled.div`
  background-color: rgba(255, 255, 255, 0.06);
  border-left: 3px solid #fab528;
  padding: 10px 12px;
  margin-bottom: 8px;
`;

const BasketItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
`;

const BasketItemName = styled.p`
  font-size: 12px;
  color: #fff;
  margin: 0;
  line-height: 1.4;
  flex: 1;

  strong {
    font-weight: bold;
  }

  span,
  a {
    color: #fab528;
  }
`;

const BasketItemFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BasketQtyControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const BasketBtn = styled.button`
  background-color: transparent;
  color: #fab528;
  border: none;
  width: 28px;
  height: 28px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  font-family: "Montserrat", sans-serif;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color ease-in-out 150ms;

  &:hover {
    background-color: rgba(250, 181, 40, 0.15);
  }
`;

const BasketQty = styled.span`
  color: #fff;
  font-size: 13px;
  min-width: 32px;
  text-align: center;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BasketLineTotal = styled.span`
  color: #fab528;
  font-size: 14px;
  font-weight: bold;
`;

const BasketRemoveBtn = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.3);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  flex-shrink: 0;
  transition: color ease-in-out 150ms;

  &:hover {
    color: #e4032d;
  }
`;

const TotalBoxContainer = styled.div`
  width: 40%;
  max-width: 100%;
  padding-left: 20px;

  @media (max-width: 900px) {
    width: 100%;
    padding-left: 0;
    padding-top: 20px;
  }
`;

const StyledTotalBox = styled.div`
  background-color: #1c242b;
  padding: 20px;
  color: #fff;
  width: 100%;
  max-width: 100%;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  position: sticky;
  top: 20px;
`;

const StyledHeader = styled.h2`
  font-size: 20px;
  line-height: 30px;
  color: #fab528;
`;

const StyledPara = styled.p`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const StyledButton = styled.input`
  background-color: #e4032d;
  color: #fff;
  padding: 10px 20px;
  border: none;
  outline: none;
  font-family: "Montserrat", sans-serif;
  width: 100%;
  text-transform: uppercase;
  cursor: pointer;
  transition: all ease-in-out 200ms;
  margin-top: 10px;
  right: 20px;
  bottom: 20px;
  width: 100%;
  min-width: 150px;
  display: flex;
  justify-content: center;

  &:hover {
    background-color: #fab528;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const PostcodeField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 10px;
`;

const PostcodeLabel = styled.label`
  font-size: 14px;
  margin-bottom: 6px;
`;

const PostcodeInput = styled.input`
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  padding: 8px 10px;
  width: 100%;
  min-width: 150px;
  text-transform: uppercase;
  text-align: right;
  border: 1px solid #fab528;
  outline: none;

  &:focus {
    border-color: #e4032d;
  }
`;

const PostcodeHint = styled.span`
  font-size: 11px;
  font-style: italic;
  margin-top: 4px;
  opacity: 0.8;
`;

const StyledLink = styled.a`
  background-color: #fab528;
  color: #e4032d;
  padding: 10px 20px;
  border: none;
  outline: none;
  font-family: "Montserrat", sans-serif;
  width: 100%;
  max-width: 200px;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  transition: all ease-in-out 200ms;
  margin-top: 10px;
  align-self: center;
  text-align: center;

  &:hover {
    background-color: #fac353;
  }
`;

const SmallPara = styled.p`
  font-size: 12px;
  margin-top: -12px;

  &.add-margin {
    margin-top: 0;
  }
`;

const StyledItalic = styled.i`
  font-style: italic;
`;

const StyledItalicSmall = styled.i`
  font-style: italic;
  font-size: 12px;
`;

const StyledHr = styled.hr`
  width: 100%;
  height: 1px;
  background-color: #fff;
  border: none;
`;

const StyledOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  background-color: #e4032d;
  align-items: center;
  justify-content: center;
  color: #fff;
  background-image: url(${HeroTwo});
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  z-index: 1000;
  transition: all ease-in-out 300ms;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-width: 100%;
    width: 600px;
    text-align: center;
  }

  &.show {
    opacity: 1;
    pointer-events: initial;
  }
`;

const StyledH2 = styled.h2`
  margin-top: -80px;
`;

const RightContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const InnerRightContainer = styled.div`
  text-align: right;
`;

const Checkbox = styled.button`
  width: 24px;
  min-height: 24px;
  display: block;
  background: transparent;
  border: solid 2px #fff;

  &:hover {
    cursor: pointer;
  }

  &.active {
    &:after {
      content: "\\2714";
      display: block;
      color: #fff;
    }
  }
`;

const DispatchDetail = styled.div`
  display: flex;
  align-self: flex-end;
  width: auto;
  max-width: 80%;
  margin-left: auto;
  margin-top: 20px;
  justify-content: flex-end;

  p {
    margin-right: 10px;
  }
`;
